import { nthLabels } from './util';

/**
 * Given an array of labels, the length of the axis, and the margins to the
 * left/right of the axis, calculate an array of labels to be displayed, as well
 * as their widths.
 *
 * If there are too many labels to show in the given space, this will return
 * every nth label, where n is the ratio of the summed length of all labels to
 * the width of the axis.
 *
 *
 * To calculate the label width:
 * * Get the midpoint of the label
 * * Get the slice width, which is the distance between this label's midpoint
 *   and an adjacent label's midpoint (can be either adjacent label since all
 *   labels are equidistant) *OR* the axis width if there is only one label
 * * Calculate total width it would take to display without truncation/wrapping
 * * Calculate the maximum space left/right it can take up, which is the minimum
 *   of the following measurement for each adjacent label:
 *   * If the adjacent label is smaller than the slice width:
 *     * The midpoint of the current label +/- (the midpoint of the adjacent
 *       label -/+ half of that label's total width)
 *   * Else
 *     * Half the slice width
 * * Take that measurement, multiply by two, and that's the width of the label
 */
export function linearLabels({ labels, axisLength, leftMargin, rightMargin }) {
  const totalLabelLength = labels.reduce(
    (sum, label) => sum + label.textWidth,
    0
  );

  const n = Math.max(1, Math.floor(totalLabelLength / axisLength - 1.5));

  const displayedLabels = [];
  nthLabels(labels, n, ({ current, previous, next }) => {
    let leftBoundary;
    let rightBoundary;

    const adjacentLabel = previous || next;
    const sliceWidth = adjacentLabel
      ? Math.abs(adjacentLabel.position - current.position)
      : axisLength;

    if (previous) {
      if (previous.textWidth < sliceWidth) {
        leftBoundary =
          current.position - previous.position - previous.textWidth / 2;
      } else {
        leftBoundary = sliceWidth / 2;
      }
    } else {
      leftBoundary = leftMargin;
    }

    if (next) {
      if (next.textWidth < sliceWidth) {
        rightBoundary = next.position - current.position - next.textWidth / 2;
      } else {
        rightBoundary = sliceWidth / 2;
      }
    } else {
      rightBoundary = rightMargin;
    }

    const padding = 5;
    if (current.textWidth > sliceWidth) {
      current.width = Math.min(leftBoundary, rightBoundary) * 2 - padding;
    } else {
      current.width = sliceWidth - padding;
    }

    displayedLabels.push(current);
  });

  return displayedLabels;
}
